import classNames from 'classnames'
import type { FC } from 'react'
import React, { useCallback, useMemo, useRef } from 'react'

import { ListContextProvider, useListContext } from './list-context'
import { LIST_THEME_KEY } from './types'
import type { ListItemProps } from './types'
import useForkRef from '../../hooks/use-fork-ref'
import useIsomorphicEffect from '../../hooks/use-isomorphic-effect'
import type { PrivateThemeUiProps } from '../../styles-system/create-component'
import createComponent from '../../styles-system/create-component'
import { EMPTY_OBJECT, isEmptyObject } from '../../utils'
import isTuiElement from '../../utils/is-theme-ui-element'
import { getPaddingX, getPaddingY } from '../../utils/props-utils'
import { Box } from '../_primitives/box'
import type { BoxProps } from '../_primitives/box/types'
import { ButtonPrivate } from '../button/button'
import type { ButtonProps } from '../button/types'

/**
 * Uses an additional container component if `ListItemSecondaryAction` is the last child.
 */
export const ListItemPrivate = React.forwardRef<
  HTMLDivElement,
  ListItemProps & PrivateThemeUiProps
>(function ListItemPrivate(props, ref) {
  const {
    __variants,
    __themeKey = LIST_THEME_KEY, // compound component may override, i.e. Select
    variant = 'listItem',
    className,
    alignItems = 'center',
    autoFocus = false,
    button = false,
    children: childrenProp,
    as: component = 'li',
    ContainerComponent = 'li',
    ContainerProps = EMPTY_OBJECT,
    dense = false,
    disabled = false,
    disableGutters = false,
    divider = false,
    selected = false,
    active,
    data = EMPTY_OBJECT,
    ...other
  } = props
  // eslint-disable-next-line no-console
  // console.log(`---- render theme-ui.list-item ----`)
  const context = useListContext()
  const listItemRef = useRef<HTMLElement | undefined>(undefined)
  useIsomorphicEffect(() => {
    if (autoFocus) {
      if (listItemRef.current) {
        listItemRef.current.focus()
      } else if (process.env.NODE_ENV !== 'production') {
        console.error(
          'Tui: unable to set focus to a ListItem whose component has not been rendered.'
        )
      }
    }
  }, [autoFocus])

  const children = React.Children.toArray(childrenProp)
  const hasSecondaryAction =
    children.length &&
    isTuiElement(children[children.length - 1], ['ListItemSecondaryAction'])

  const handleOwnRef = useCallback((instance: HTMLElement) => {
    listItemRef.current = instance
  }, [])

  const handleRef = useForkRef(handleOwnRef, ref)

  const combinePaddingX = {
    ...getPaddingX(props.__css ?? {}),
    ...getPaddingX(props),
    ...getPaddingX(props.sx ?? {})
  }

  const combinePaddingY = {
    ...getPaddingY(props.__css ?? {}),
    ...getPaddingY(props),
    ...getPaddingY(props.sx ?? {})
  }

  const componentProps: BoxProps<HTMLLIElement> &
    PrivateThemeUiProps & {
      disabled?: boolean
      autoFocus?: boolean
      selected?: boolean
      data?: object
    } = {
    variant:
      __themeKey === LIST_THEME_KEY && !button // independent list component i.e. not part of other compound component
        ? context.variant
          ? `${context.variant}.listItem`
          : variant
        : variant,
    __themeKey: !button ? __themeKey : undefined,
    className: classNames(
      {
        'csg-tui-list-item': true,
        'is-active': active,
        'is-selected': selected,
        'is-disabled': disabled,
        'is-dense': dense
      },
      className
    ),
    __css: {
      display: 'flex',
      position: 'relative',
      justifyContent: 'flex-start',
      alignItems,
      textDecoration: 'none',
      width: '100%',
      textAlign: 'left',
      userSelect: 'none',
      ...(button && isEmptyObject(combinePaddingY)
        ? dense
          ? { emPaddingY: 'xs-3' }
          : { emPaddingY: 'xs-2' }
        : {}),
      ...(!disableGutters && isEmptyObject(combinePaddingX)
        ? dense
          ? { emPaddingX: 'xxs' }
          : { emPaddingX: 'xs' }
        : {}),
      ...(divider
        ? {
            borderBottom: 'neutral.light',
            backgroundClip: 'padding-box'
          }
        : {})
    },
    disabled,
    autoFocus,
    selected,
    ...(isEmptyObject(data) ? {} : { data }),
    ...other
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let Component = useMemo(
    () =>
      button
        ? React.forwardRef<HTMLButtonElement, ButtonProps>(
            function ListItemButton(props, ref) {
              return <ButtonPrivate as={component} ref={ref} {...props} />
            }
          )
        : typeof component === 'object'
          ? component
          : createComponent(component, 'csg-list-item', __variants),
    [__variants, button, component]
  )
  // eslint-disable-next-line no-console
  // console.log(`----- render list-item -----`)
  if (hasSecondaryAction) {
    // Use div by default.
    Component = !componentProps.as && !component ? 'div' : Component

    // Avoid nesting of li > li.
    if (ContainerComponent === 'li') {
      if (component === 'li') {
        Component = Box
      } else if (componentProps.as === 'li') {
        componentProps.as = 'div'
      }
    }

    return (
      <ListContextProvider
        dense={dense || context.dense || false}
        alignItems={alignItems}
        variant={!button ? variant : undefined}
      >
        <ContainerComponent relative ref={handleRef} {...ContainerProps}>
          <Component
            {...{ ...componentProps, ...(button ? { disabled } : {}) }}
          >
            {children}
          </Component>
          {children.pop()}
        </ContainerComponent>
      </ListContextProvider>
    )
  }

  return (
    <ListContextProvider
      dense={dense || context.dense || false}
      alignItems={alignItems}
      variant={!button ? variant : undefined}
    >
      <Component
        ref={handleRef}
        {...{ ...componentProps, ...(button ? { disabled } : {}) }}
      >
        {children}
      </Component>
    </ListContextProvider>
  )
})

const MemoList = React.memo(ListItemPrivate)

const ListItem: FC<ListItemProps> = React.forwardRef<
  HTMLLIElement,
  ListItemProps
>(function ListItem(props, ref) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return <MemoList ref={ref as any} {...props} />
})

export default ListItem
